import React from "react";

const ChromeImg = ({ src, alt }) => (
  <div className="browserchrome my-4">
    <img src={src} alt={alt || ""} />
  </div>
);

const P = ({ children }) => (
  <p className="text-lg mb-4 leading-normal">{children}</p>
);

const A = ({ children, href, target }) => (
  <a className="text-blue-500" target={target} href={href}>
    {children}
  </a>
);

export default () => {
  return (
    <div className="md:my-8 md:w-1/2 mx-auto">
      <h1 className="text-center text-5xl mb-6 leading-none">
        How can I get a handle on how much money I'm spending?
      </h1>
      <P>
        Being an adult is hard. You have to buy all your own stuff, make the
        money to buy the stuff, and set aside time to actually live your life.
      </P>
      <P>Why didn't they teach any of this in school?!</P>
      <P>
        What if you could see, at a glance, how much money you'd spent each
        month, and where you were spending <i>too much</i> so you knew where to
        cut back if you wanted a little extra cash?
      </P>
      <P>Something like this:</P>
      <ChromeImg src="https://i.imgur.com/Z4ksDo6.png" alt="result table" />
      <P>
        Well that sure would be nice. This person is spending is spending quite
        a bit on travel. If they wanted to hit financial independence earlier,
        they definitely have room to cut back on that.
      </P>
      <P>
        Also, what on earth happened with Entertainment in April?! That's way
        more than usual.
      </P>
      <P>
        At a baseline, though, spending $2.5k in February and March looks great.
        If they could keep that level of spending up, they would only need to
        save <code>$2,500 &times; 12 months/year * 25 = </code>
        <b>$750,000</b> to retire and live on the interest forever.
      </P>
      <P>
        On a salary of $60k/year, they could save $750,000 in{" "}
        <b>just 18 years</b>. If they could get a raise and hit that sweet
        six-figure mark, that number drops to <b>a nine year working career</b>{" "}
        to hit retirement.
      </P>
      <P>
        <i>
          (Curious about how that retirement math works? Check out{" "}
          <A href="https://www.mrmoneymustache.com/2012/01/13/the-shockingly-simple-math-behind-early-retirement/">
            The Shockingly Simple Math Behind Early Retirement
          </A>
        </i>
      </P>
      <h2>
        That's right - I just made a retirement plan from this single table of
        numbers.
      </h2>
      <P>
        Want a fancy table like this for yourself? I'll help you make one in
        five minutes. You'll be totally in control of your financial data the
        entire time (I'm won't  ask you for any bank passwords).
      </P>
      <P>
        First,{" "}
        <A
          href="https://airtable.com/invite/l?inviteId=inveHmj1XgbuWMRaR&inviteToken=6478f9c7a316281ffbae448ed3b78ecd2b097491cdca0485bf323c173086af19"
          target="_blank"
        >
          open this airtable
        </A>
        . Airtables are like spreadsheets but better. If you don't have an
        Airtable account, it takes about ten seconds to make one:
      </P>
      <ChromeImg src="https://i.imgur.com/CRbIwk5.gif" alt="signup" />
      <P>
        Once you're in Airtable, clone it by opening the "gen.co/expenses" base,
        then clicking on the down arrow by it's title, then clicking "duplicate
        base."
      </P>
      <ChromeImg src="https://i.imgur.com/a0Vselc.gif" alt="duplicate" />
      <P>
        This is copy is the base you'll be entering all your financial
        transactions into, so you might want to rename it to something prettier
        than "gen.co/expenses template copy."
      </P>
      <ChromeImg src="https://i.imgur.com/jnn1fpg.gif" alt="rename" />
      <P>This Airtable base has six columns:</P>
      <ul>
        <li>
          <b>date</b>: when this transaction happened
        </li>
        <li>
          <b>amount</b>: how much this transaction is for. Most of these numbers
          will be negative
        </li>
        <li>
          <b>description</b>: the raw description of this transaction from your
          bank
        </li>
        <li>
          <b>note</b>: if the description is vague, you can include a personal
          note to yourself so you can remember later what this transaction was
          for
        </li>
        <li>
          <b>category</b>: what area of spending this transaction was. You can
          make up your own!
        </li>
        <li>
          <b>source</b>: the bank you got this information from
        </li>
      </ul>
      <ChromeImg
        src="https://i.imgur.com/lVQbihU.png"
        alt="Columns of information in the airtable base"
      />
      <P>
        You can fill this table with your own transactions later. Most banks
        have a "export the last month's transactions as a CSV/Excel table"
        feature, so you can paste your transactions in a month at a time and
        then manually categorize them.
      </P>
      <P>
        For now, let's get this Airtable base hooked up to show you a fancy
        chart! (we're almost done!)
      </P>
      <P>
        We need two things to connect Airtable: an <code>API key</code>, and a{" "}
        <code>Base ID</code>.
      </P>
      <P>
        Get your <code>API key</code> from{" "}
        <A href="https://airtable.com/account">the Airtable account page</A> by
        clicking "Generate API key." It should start with <code>key</code>.
      </P>
      <ChromeImg src="https://i.imgur.com/TgK8267.gif" alt="Generate API key" />
      <P>
        Get your <code>Base ID</code> from{" "}
        <A href="https://airtable.com/api">the Airtable API page</A> for your
        base. It will be hidden in the commands on the right side of the screen
        immediately after:{" "}
      </P>
      <pre className="overflow-y-scroll">curl https://api.airtable.com/v0/</pre>
      <P>and right before a slash, similar to this:</P>
      <pre className="overflow-y-scroll">
        $ curl https://api.airtable.com/v0/YOUR_BASE_ID/Expenses
      </pre>
      <ChromeImg src="https://i.imgur.com/RXWYu5e.gif" alt="Get the base ID" />
      <P>
        Paste those two things in this form, hit "save", and we'll be rockin'
        and rollin':
      </P>
    </div>
  );
};
